import { useCallback, useEffect, useRef } from 'react'
import { bufferEvent, flushEvents, flushEventsKeepalive } from '@entities/analytics'
import {
  clampDuration,
  nextTimeReportDelay,
} from '../lib/pageTimeSchedule'
import { CODE_MAX_LENGTH } from '../types'

/**
 * Обрезаем коды до предела сервера: превышение роняет весь запрос целиком,
 * вместе с чужими событиями в том же батче.
 */
const toCode = (value: string): string =>
  value.trim().slice(0, CODE_MAX_LENGTH)

/**
 * Аналитика одной страницы: открытие, время и действия.
 *
 * Время считаем по `performance.now()`, а не по часам: системное время может
 * переводиться, и отрезки выходили бы отрицательными или огромными. Пока
 * страница в фоне, время не идёт — таймер останавливается, а по возвращении
 * расписание начинается заново, без нового `page_open`.
 *
 * @param page код страницы: `foodpairing`, `recipe/123`
 * @param enabled можно отложить старт, пока страница ещё грузится
 */
export const usePageAnalytics = (page: string, enabled = true) => {
  const code = toCode(page)

  // Начало текущего отрезка расписания и момент прошлой отправки времени.
  const phaseStartRef = useRef(0)
  const lastSentRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  /** Отправить накопленное время. Возвращает, было ли что отправлять. */
  const reportTime = useCallback((): boolean => {
    const now = performance.now()
    const duration = clampDuration(now - lastSentRef.current)
    lastSentRef.current = now
    if (duration <= 0) return false

    bufferEvent({ name: 'page_time', page: code, duration_ms: duration })
    return true
  }, [code])

  const scheduleNext = useCallback(() => {
    clearTimer()
    const elapsed = performance.now() - phaseStartRef.current
    timerRef.current = setTimeout(() => {
      reportTime()
      // Отправляем очередь по тому же таймеру: отдельный запрос на каждое
      // действие сжёг бы лимит, а копить до конца сессии — потерять хвост.
      flushEvents()
      scheduleNext()
    }, nextTimeReportDelay(elapsed))
  }, [reportTime])

  // Жизнь страницы: открытие, уход, сворачивание.
  useEffect(() => {
    if (!enabled || !code) return

    const startPhase = () => {
      const now = performance.now()
      phaseStartRef.current = now
      lastSentRef.current = now
      scheduleNext()
    }

    bufferEvent({ name: 'page_open', page: code })
    startPhase()

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimer()
        reportTime()
        // Уходим из приложения — обычный запрос оборвётся вместе со страницей.
        flushEventsKeepalive()
      } else {
        // Вернулись на ту же страницу: нового захода не было, расписание
        // начинается сначала.
        startPhase()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      clearTimer()
      reportTime()
      // Уход на другую страницу — остаток времени отправляем сразу, иначе он
      // уедет уже с кодом следующей страницы в соседнем событии.
      flushEvents()
    }
  }, [code, enabled, reportTime, scheduleNext])

  /** Действие на странице. Объект действия кладём в `value`, а не в код. */
  const trackAction = useCallback(
    (action: string, value?: string) => {
      if (!code) return
      const event: {
        name: 'page_action'
        page: string
        action: string
        value?: string
      } = { name: 'page_action', page: code, action: toCode(action) }

      const objectCode = value ? toCode(value) : ''
      if (objectCode) event.value = objectCode

      bufferEvent(event)
    },
    [code]
  )

  return { trackAction }
}
